import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Boxes,
  CalendarPlus,
  History,
  Loader2,
  Pencil,
  Plus,
  Settings2,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { api, type Organization, type X5User } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Badge } from '../components/ui/Badge';
import { UserDetailDrawer } from '../components/UserDetailDrawer';
import { AddOrgMembersDrawer, type MembershipKind } from '../components/AddOrgMembersDrawer';
import { formatDateTime, formatRelative } from '../lib/format';
import { useAuth } from '../contexts/use-auth';

export function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const canEditDetails = can('x5.org.write');
  const canEditMembers = can('x5.org.members');

  const queryClient = useQueryClient();
  const [adding, setAdding] = useState<MembershipKind | null>(null);
  const [openedUser, setOpenedUser] = useState<X5User | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  // Per-section, and off by default: the Remove buttons stay out of the way
  // until you say you are here to change membership. Users and applications
  // toggle independently — managing one is not a reason to arm the other.
  const [managing, setManaging] = useState<Record<MembershipKind, boolean>>({
    users: false,
    applications: false,
  });

  const toggleManaging = (kind: MembershipKind) => {
    setRemoveError(null);
    setManaging((prev) => ({ ...prev, [kind]: !prev[kind] }));
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['organization', id],
    queryFn: () => api.getOrganization(id!),
    enabled: Boolean(id),
  });

  const removeMember = useMutation({
    mutationFn: (input: { kind: MembershipKind; id: string }) =>
      api.updateOrganizationMembers(
        id!,
        input.kind === 'users'
          ? { removeUserIds: [input.id] }
          : { removeApplicationIds: [input.id] },
      ),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['organization', id] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      const failure = result.failed[0];
      setRemoveError(failure ? failure.reason : null);
    },
    onError: (err) => setRemoveError((err as Error).message),
  });

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (error) return <p className="text-destructive">{(error as Error).message}</p>;
  if (!data) return null;

  const { organization, users, applications } = data;
  // Remove only appears once the section is in manage mode, and only for
  // someone allowed to change membership.
  const removable = {
    users: canEditMembers && managing.users,
    applications: canEditMembers && managing.applications,
  };
  const orgName = organization.name ?? organization.company ?? organization.id;

  /**
   * Detaching does not delete the record, and saying so is the point of the
   * prompt — "remove" next to a customer's name reads more final than it is.
   */
  function confirmRemove(kind: MembershipKind, memberId: string, label: string) {
    const noun = kind === 'users' ? 'user' : 'application';
    const consequence =
      kind === 'users'
        ? `${label} will no longer see this organization's data in X5.`
        : `${label} will no longer appear under this organization in X5.`;
    const lines = [
      `Remove ${label} from ${orgName}?`,
      '',
      consequence,
      `The ${noun} record itself is kept — it is left unassigned and can be added back at any time.`,
    ];
    if (confirm(lines.join('\n'))) {
      setRemoveError(null);
      removeMember.mutate({ kind, id: memberId });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/organizations"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Organizations
        </Link>
        <OrgHeader organization={organization} canEdit={canEditDetails} />
      </div>

      {removeError && (
        <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Remove failed</p>
            <p className="mt-1 text-xs">{removeError}</p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Boxes className="h-4 w-4 text-muted-foreground" />
            Applications
            <span className="text-xs font-normal text-muted-foreground">
              {applications.length}
            </span>
          </CardTitle>
          {canEditMembers && (
            <MemberActions
              managing={managing.applications}
              addLabel="Add applications"
              onAdd={() => setAdding('applications')}
              onToggleManaging={() => toggleManaging('applications')}
            />
          )}
        </CardHeader>
        <CardContent className="px-0 pb-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-y border-border bg-muted/50">
                <tr className="text-left text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Display</th>
                  <th className="px-6 py-3 font-medium">Application</th>
                  <th className="px-6 py-3 font-medium">Stack / Fleet</th>
                  <th className="px-6 py-3 font-medium">Server</th>
                  {removable.applications && <th className="px-6 py-3" />}
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-border transition-colors last:border-0 hover:bg-muted/50"
                  >
                    <td className="px-6 py-3">{a.displayName ?? '—'}</td>
                    <td className="px-6 py-3 font-mono text-xs">{a.application ?? '—'}</td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {a.stack ?? '—'} / {a.fleet ?? '—'}
                    </td>
                    <td className="px-6 py-3 font-mono text-xs">{a.x5serverIP ?? '—'}</td>
                    {removable.applications && (
                      <td className="px-6 py-3 text-right">
                        <RemoveButton
                          pending={
                            removeMember.isPending && removeMember.variables?.id === a.id
                          }
                          disabled={removeMember.isPending}
                          onClick={() =>
                            confirmRemove(
                              'applications',
                              a.id,
                              a.displayName ?? a.application ?? a.id,
                            )
                          }
                        />
                      </td>
                    )}
                  </tr>
                ))}
                {applications.length === 0 && (
                  <tr>
                    <td
                      className="px-6 py-6 text-center text-muted-foreground"
                      colSpan={removable.applications ? 5 : 4}
                    >
                      No applications.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <UsersIcon className="h-4 w-4 text-muted-foreground" />
            Users
            <span className="text-xs font-normal text-muted-foreground">{users.length}</span>
          </CardTitle>
          {canEditMembers && (
            <MemberActions
              managing={managing.users}
              addLabel="Add users"
              onAdd={() => setAdding('users')}
              onToggleManaging={() => toggleManaging('users')}
            />
          )}
        </CardHeader>
        <CardContent className="px-0 pb-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-y border-border bg-muted/50">
                <tr className="text-left text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Username</th>
                  <th className="px-6 py-3 font-medium">ID</th>
                  <th className="px-6 py-3 font-medium">Created</th>
                  {removable.users && <th className="px-6 py-3" />}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => setOpenedUser(u)}
                    className="cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/50"
                  >
                    <td className="px-6 py-3 font-medium">{u.username ?? '—'}</td>
                    <td className="px-6 py-3 font-mono text-xs text-muted-foreground">{u.id}</td>
                    <td className="px-6 py-3 text-muted-foreground">
                      <span title={u.createdAt ?? ''}>{formatRelative(u.createdAt)}</span>
                    </td>
                    {removable.users && (
                      <td className="px-6 py-3 text-right">
                        <RemoveButton
                          pending={
                            removeMember.isPending && removeMember.variables?.id === u.id
                          }
                          disabled={removeMember.isPending}
                          // The row opens the user drawer; the button must not.
                          onClick={() => confirmRemove('users', u.id, u.username ?? u.id)}
                          stopPropagation
                        />
                      </td>
                    )}
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td
                      className="px-6 py-6 text-center text-muted-foreground"
                      colSpan={removable.users ? 4 : 3}
                    >
                      No users.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {adding && id && (
        <AddOrgMembersDrawer
          kind={adding}
          organizationId={id}
          organizationName={orgName}
          onClose={() => setAdding(null)}
        />
      )}

      {openedUser && (
        <UserDetailDrawer user={openedUser} onClose={() => setOpenedUser(null)} />
      )}
    </div>
  );
}

/**
 * Card-header controls for a membership section. Adding and removing are
 * separate modes rather than two buttons side by side: while you are removing,
 * Add would only be a way to lose your place.
 */
function MemberActions({
  managing,
  addLabel,
  onAdd,
  onToggleManaging,
}: {
  managing: boolean;
  addLabel: string;
  onAdd: () => void;
  onToggleManaging: () => void;
}) {
  if (managing) {
    return (
      <Button variant="outline" size="sm" onClick={onToggleManaging}>
        <X className="h-3.5 w-3.5" />
        Done managing
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={onAdd}>
        <Plus className="h-3.5 w-3.5" />
        {addLabel}
      </Button>
      <Button variant="outline" size="sm" onClick={onToggleManaging}>
        <Settings2 className="h-3.5 w-3.5" />
        Manage
      </Button>
    </div>
  );
}

function RemoveButton({
  pending,
  disabled,
  onClick,
  stopPropagation,
}: {
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
  stopPropagation?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={disabled}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
        onClick();
      }}
      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
    >
      {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
      Remove
    </Button>
  );
}

function OrgHeader({
  organization,
  canEdit,
}: {
  organization: Organization;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(organization.name ?? '');
  const [company, setCompany] = useState(organization.company ?? '');
  const [description, setDescription] = useState(organization.description ?? '');

  function startEditing() {
    setName(organization.name ?? '');
    setCompany(organization.company ?? '');
    setDescription(organization.description ?? '');
    save.reset();
    setEditing(true);
  }

  const save = useMutation({
    // Only the fields that actually differ are sent; the server ignores keys it
    // is not given, so an untouched field is never written.
    mutationFn: () =>
      api.updateOrganization(organization.id, {
        ...(name.trim() !== (organization.name ?? '') ? { name: name.trim() } : {}),
        ...(company.trim() !== (organization.company ?? '')
          ? { company: company.trim() || null }
          : {}),
        ...(description.trim() !== (organization.description ?? '')
          ? { description: description.trim() || null }
          : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organization', organization.id] });
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setEditing(false);
    },
  });

  const dirty =
    name.trim() !== (organization.name ?? '') ||
    company.trim() !== (organization.company ?? '') ||
    description.trim() !== (organization.description ?? '');

  const lastChangedIso = organization._lastChangedAt
    ? new Date(organization._lastChangedAt).toISOString()
    : null;

  if (editing) {
    return (
      <div className="mt-3 max-w-2xl space-y-3">
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            autoFocus
            placeholder="Organization name"
          />
        </Field>
        <Field label="Company">
          <Input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            maxLength={200}
            placeholder="Leave blank to clear"
          />
        </Field>
        <Field label="Description">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
            placeholder="Leave blank to clear"
          />
        </Field>

        {save.isError && (
          <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Save failed</p>
              <p className="mt-1 text-xs">{(save.error as Error).message}</p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            onClick={() => save.mutate()}
            disabled={!dirty || name.trim().length === 0 || save.isPending}
          >
            {save.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
          <Button variant="outline" onClick={() => setEditing(false)} disabled={save.isPending}>
            Cancel
          </Button>
          {name.trim().length === 0 && (
            <span className="text-xs text-muted-foreground">A name is required.</span>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mt-2 flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold tracking-tight">
          {organization.name ?? '(no name)'}
        </h1>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={startEditing}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        {organization.company && <span>{organization.company}</span>}
        {organization.company && <span>·</span>}
        <span className="font-mono text-xs">{organization.id}</span>
      </div>
      {organization.description && (
        <p className="mt-3 text-sm text-foreground">{organization.description}</p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
        <Stamp
          icon={CalendarPlus}
          label="Added"
          iso={organization.createdAt ?? null}
          fallback="not recorded"
        />
        <Stamp
          icon={History}
          label="Updated"
          iso={organization.updatedAt ?? lastChangedIso}
          fallback="never"
        />
        {organization._version != null && (
          <Badge variant="outline" className="text-[10px]">
            v{organization._version}
          </Badge>
        )}
      </div>
    </>
  );
}

function Stamp({
  icon: Icon,
  label,
  iso,
  fallback,
}: {
  icon: typeof CalendarPlus;
  label: string;
  iso: string | null;
  fallback: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5" title={iso ?? ''}>
      <Icon className="h-3.5 w-3.5" />
      <span className="font-medium text-fg-2">{label}</span>
      {iso ? (
        <span>
          {formatRelative(iso)} · {formatDateTime(iso)}
        </span>
      ) : (
        <span className="italic">{fallback}</span>
      )}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
